/**
 * CapsuleShadowsNode — analytic character shadows from capsule proxies (the
 * UE Capsule Shadows / TLOU analytic approach): each character is a handful
 * of capsules; a post pass reconstructs the world position per pixel, casts
 * a ray toward the light, and darkens by the capsules' angular occlusion of
 * that ray — soft, cheap, works in baked/indirect lighting where shadow maps
 * have nothing to offer.
 *
 * Occlusion model (per capsule): treat the closest point of the capsule
 * segment to the shadow ray as a sphere of the capsule radius; the sphere's
 * angular radius α = asin(r/d) versus the ray's angular miss θ gives
 *   occ = 1 − linstep(α, α + penumbra, θ)
 * scaled by the light-facing test. The maximum over capsules wins (limbs
 * overlap; summing would over-darken).
 */

import type * as THREE from 'three/webgpu';
import { Vector3, Matrix4 } from 'three/webgpu';
import {
  Fn, If, uniform, float, vec2, vec3, vec4, uv, min, max, clamp, saturate, select,
  normalize, acos, asin, dot, perspectiveDepthToViewZ, cameraPosition, cameraWorldMatrix,
} from 'three/tsl';

export const MAX_SHADOW_CAPSULES = 8;

export interface CapsuleDef {
  a: [number, number, number];
  b: [number, number, number];
  radius: number;
}

export interface CapsuleShadowsPassConfig {
  /** World-space light direction the shadows cast AWAY from (default sun-ish). */
  lightDirection?: [number, number, number];
  /** Maximum darkening 0..1 (default 0.65). */
  intensity?: number;
  /** Penumbra angle in radians added past the hard cone (default 0.25). */
  penumbra?: number;
  /** Occlusion fades out past this distance from the capsule (default 6). */
  maxDistance?: number;
  enabled?: boolean;
  /**
   * EXPLICIT CAMERA (sky/post 2026-09-27): read the scene camera's render-space position and world matrix from
   * `uniforms.cameraPos` / `uniforms.cameraWorld` (the caller writes them every frame) instead of the ambient TSL
   * camera nodes — this pass runs in the post chain's output QUAD, where `cameraPosition` / `cameraWorldMatrix` are
   * the quad's own orthographic camera (the height fog's 2026-09-25 WHY): the reconstructed "world" point was a
   * view-space point and every capsule occlusion was tested at the wrong place. Default false keeps the old
   * behaviour for callers that never write the uniforms.
   */
  explicitCamera?: boolean;
}

export interface CapsuleShadowsUniforms {
  lightDirection: { value: THREE.Vector3 };
  intensity: { value: number };
  penumbra: { value: number };
  maxDistance: { value: number };
  near: { value: number };
  far: { value: number };
  tanHalfFovY: { value: number };
  aspect: { value: number };
  capsuleA: Array<{ value: THREE.Vector3 }>;
  capsuleB: Array<{ value: THREE.Vector3 }>;
  capsuleRadius: Array<{ value: number }>;
  setCapsule(index: number, capsule: CapsuleDef | null): void;
  /** The scene camera (explicitCamera): render-space position + world matrix — write every frame. */
  cameraPos: { value: THREE.Vector3 };
  cameraWorld: { value: THREE.Matrix4 };
}

// ─── CPU mirror (exported for tests) ────────────────────────────────────────

/**
 * Occlusion of a shadow ray (origin p, direction l, unit) by a sphere at c
 * with radius r: angular-overlap soft term.
 */
export function sphereRayOcclusionCpu(
  p: [number, number, number], l: [number, number, number],
  c: [number, number, number], r: number,
  penumbra: number, maxDistance: number,
): number {
  const to = [c[0] - p[0], c[1] - p[1], c[2] - p[2]];
  const d = Math.hypot(to[0], to[1], to[2]);
  if (d < 1e-6) return 1; // inside the occluder
  if (d > maxDistance) return 0;
  const cosT = (to[0] * l[0] + to[1] * l[1] + to[2] * l[2]) / d;
  if (cosT <= 0) return 0; // occluder behind the surface w.r.t. the light
  const theta = Math.acos(Math.min(Math.max(cosT, -1), 1));
  const alpha = Math.asin(Math.min(r / d, 1));
  const t = (theta - alpha) / Math.max(penumbra, 1e-4);
  const s = t < 0 ? 0 : (t > 1 ? 1 : t);
  const distFade = 1 - Math.min(d / maxDistance, 1);
  return (1 - s * s * (3 - 2 * s)) * distFade;
}

/** Closest point on segment ab to point p (CPU, exported for tests). */
export function closestPointOnSegmentCpu(
  p: [number, number, number], a: [number, number, number], b: [number, number, number],
): [number, number, number] {
  const ab = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const ap = [p[0] - a[0], p[1] - a[1], p[2] - a[2]];
  const len2 = ab[0] * ab[0] + ab[1] * ab[1] + ab[2] * ab[2];
  const t = len2 < 1e-9 ? 0 : Math.min(Math.max(
    (ap[0] * ab[0] + ap[1] * ab[1] + ap[2] * ab[2]) / len2, 0), 1);
  return [a[0] + ab[0] * t, a[1] + ab[1] * t, a[2] + ab[2] * t];
}

/**
 * The point of segment ab nearest the shadow RAY p + l·t (l unit) — the occluder the ray actually passes (CPU mirror
 * of the shader; shader sweep 2026-10-06). A segment parallel to the ray (sin² of the angle under 1e-4) falls back to
 * the point nearest p.
 */
export function closestPointOnSegmentToRayCpu(
  p: [number, number, number], l: [number, number, number], a: [number, number, number], b: [number, number, number],
): [number, number, number] {
  const u = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
  const w = [a[0] - p[0], a[1] - p[1], a[2] - p[2]];
  const A = u[0] * u[0] + u[1] * u[1] + u[2] * u[2];
  const B = u[0] * l[0] + u[1] * l[1] + u[2] * l[2];
  const D = u[0] * w[0] + u[1] * w[1] + u[2] * w[2];
  const E = l[0] * w[0] + l[1] * w[1] + l[2] * w[2];
  const denom = A - B * B;
  if (!(denom > A * 1e-4)) return closestPointOnSegmentCpu(p, a, b);
  const s = Math.min(Math.max((B * E - D) / denom, 0), 1);
  return [a[0] + u[0] * s, a[1] + u[1] * s, a[2] + u[2] * s];
}

/** One capsule's occlusion of the shadow ray from p toward the light l — the shader's per-slot term (CPU mirror). */
export function capsuleOcclusionCpu(
  p: [number, number, number], l: [number, number, number], a: [number, number, number], b: [number, number, number],
  r: number, penumbra: number, maxDistance: number,
): number {
  if (!(r > 0)) return 0;
  return sphereRayOcclusionCpu(p, l, closestPointOnSegmentToRayCpu(p, l, a, b), r, penumbra, maxDistance);
}

// ─── Pass factory ───────────────────────────────────────────────────────────

export function createCapsuleShadowsNode(
  input: unknown,
  depthNode: unknown,
  camera: THREE.Camera,
  config?: CapsuleShadowsPassConfig,
): { node: unknown; uniforms: CapsuleShadowsUniforms } {
  const cam = camera as THREE.PerspectiveCamera;
  const ld = config?.lightDirection ?? [0.3, 0.8, 0.5];
  const uLightDir = uniform(vec3(ld[0], ld[1], ld[2]));
  const uIntensity = uniform(config?.intensity ?? 0.65);
  const uPenumbra = uniform(config?.penumbra ?? 0.25);
  const uMaxDist = uniform(config?.maxDistance ?? 6);
  const uNear = uniform(cam.near ?? 0.1);
  const uFar = uniform(cam.far ?? 1000);
  const uTanHalf = uniform(Math.tan(((cam.fov ?? 60) * Math.PI) / 360));
  const uAspect = uniform(cam.aspect ?? 1);
  const uCamPos = uniform(new Vector3());
  const uCamWorld = uniform(new Matrix4());
  const camPosN = config?.explicitCamera ? uCamPos : cameraPosition;
  const camWorldN = config?.explicitCamera ? uCamWorld : cameraWorldMatrix;

  const capsuleA: Array<ReturnType<typeof uniform>> = [];
  const capsuleB: Array<ReturnType<typeof uniform>> = [];
  const capsuleRadius: Array<ReturnType<typeof uniform>> = [];
  for (let i = 0; i < MAX_SHADOW_CAPSULES; i++) {
    capsuleA.push(uniform(vec3(0, 0, 0)));
    capsuleB.push(uniform(vec3(0, 0, 0)));
    capsuleRadius.push(uniform(0));
  }

  const build = Fn(() => {
    const cIn = vec4(input as never);
    // THE LAZY-SAMPLE LAW (2026-09-29, the height fog's bug): the depth sample is a VAR at the top of the function, so
    // its one emission never lands inside a branch that another path then reads uninitialised.
    const depthScalar = float(((depthNode as { r?: unknown }).r ?? depthNode) as never).toVar();
    const viewZ = perspectiveDepthToViewZ(depthScalar, uNear, uFar).toVar();
    // sky/post (2026-09-27): the post quad's uv() is TOP-origin (THE POST-UV LAW — the height fog's night band), so NDC
    // y is 1 − 2v; the old 2v − 1 mirrored the reconstruction vertically (a pixel's point came from its mirror row).
    const ndc = vec2(uv().x.mul(2).sub(1), float(1).sub(uv().y.mul(2)));
    const rayView = vec3(ndc.x.mul(uTanHalf).mul(uAspect), ndc.y.mul(uTanHalf), float(-1));
    const worldDir = normalize((camWorldN as typeof cameraWorldMatrix).mul(vec4(rayView, 0)).xyz);
    const dist = viewZ.negate().mul(rayView.length()).toVar();
    // THE LAZY-VAR LAW: both are read inside every slot's branch below — placed here, at the top, so neither is first
    // emitted inside slot 0's block (out of scope for slot 1's).
    const worldPos = (camPosN as typeof cameraPosition).add(worldDir.mul(dist)).toVar();
    const l = normalize(uLightDir).toVar();

    const occ = float(0).toVar();
    for (let i = 0; i < MAX_SHADOW_CAPSULES; i++) {
      const a = capsuleA[i] as never as ReturnType<typeof vec3>;
      const b = capsuleB[i] as never as ReturnType<typeof vec3>;
      const r = capsuleRadius[i] as never as ReturnType<typeof float>;
      // WHY the branch (shader sweep 2026-10-06): an empty slot contributes exactly nothing, and Atlas fills ONE of the
      // eight — the old unrolled loop paid two inverse trig calls and a sqrt for all eight on every pixel. `r` is a
      // uniform, so the branch is coherent: the GPU skips the empty slots outright.
      If(r.greaterThan(0), () => {
        // WHY the ray's point (shader sweep 2026-10-06): the header's model takes the capsule point nearest the SHADOW
        // RAY; the shader took the point nearest the SURFACE point — a standing capsule's bottom end for every ground
        // point, so at a low sun the shadow ended after the feet (a point the sun ray reaches straight through the body
        // read ~0.2 occlusion). Closest points of the segment and the line p + l·t; a segment parallel to the ray
        // falls back to the point nearest the surface (`closestPointOnSegmentToRayCpu` is the mirror).
        const ab = b.sub(a);
        const w = a.sub(worldPos);
        const A = dot(ab, ab);
        const B = dot(ab, l);
        const D = dot(ab, w);
        const E = dot(l, w);
        const denom = A.sub(B.mul(B));
        const sLine = B.mul(E).sub(D).div(denom.max(1e-12));
        const sPoint = D.negate().div(A.add(1e-9));
        const t = clamp(select(denom.greaterThan(A.mul(1e-4)), sLine, sPoint), float(0), float(1));
        const c = a.add(ab.mul(t));
        const to = c.sub(worldPos);
        const d = to.length().add(1e-6);
        const cosT = saturate(dot(to.div(d), l));
        const theta = acos(clamp(cosT, float(-1), float(1)));
        const alpha = asin(clamp(r.div(d), float(0), float(1)));
        const s = clamp(theta.sub(alpha).div(uPenumbra), float(0), float(1));
        const smooth = s.mul(s).mul(float(3).sub(s.mul(2)));
        const distFade = saturate(float(1).sub(d.div(uMaxDist)));
        occ.assign(max(occ, float(1).sub(smooth).mul(distFade)));
      });
    }

    const shade = float(1).sub(min(occ.mul(uIntensity), uIntensity));
    return vec4(cIn.rgb.mul(shade), cIn.a);
  });

  const asVec3 = (u: ReturnType<typeof uniform>): { value: THREE.Vector3 } =>
    u as unknown as { value: THREE.Vector3 };
  const asNum = (u: ReturnType<typeof uniform>): { value: number } =>
    u as unknown as { value: number };

  const uniforms: CapsuleShadowsUniforms = {
    lightDirection: asVec3(uLightDir),
    intensity: asNum(uIntensity),
    penumbra: asNum(uPenumbra),
    maxDistance: asNum(uMaxDist),
    near: asNum(uNear),
    far: asNum(uFar),
    tanHalfFovY: asNum(uTanHalf),
    aspect: asNum(uAspect),
    capsuleA: capsuleA.map(asVec3),
    capsuleB: capsuleB.map(asVec3),
    capsuleRadius: capsuleRadius.map(asNum),
    cameraPos: uCamPos as unknown as { value: THREE.Vector3 },
    cameraWorld: uCamWorld as unknown as { value: THREE.Matrix4 },
    setCapsule(index: number, capsule: CapsuleDef | null): void {
      if (index < 0 || index >= MAX_SHADOW_CAPSULES) return;
      if (!capsule) {
        this.capsuleRadius[index].value = 0;
        return;
      }
      this.capsuleA[index].value.set(...capsule.a);
      this.capsuleB[index].value.set(...capsule.b);
      this.capsuleRadius[index].value = capsule.radius;
    },
  };

  return { node: build(), uniforms };
}
